/**
 * Driver Registration — Multi-Step
 * تسجيل سائق جديد بخطوات متعددة مع المتطلبات
 */
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import '../../DriverApp.css';

const API = process.env.NODE_ENV === 'development'
  ? 'http://localhost:8002/api/v1/driver'
  : '/api/v1/driver';

const GROUPS_SHOW = ['identity', 'driving_license']; // groups shown at registration time

const STEPS = [
  { id: 1, title: 'المعلومات الأساسية', icon: '👤' },
  { id: 2, title: 'الوثائق المطلوبة',  icon: '📄' },
  { id: 3, title: 'تأكيد التسجيل',     icon: '✅' },
];

export default function RegisterPage() {
  const [step, setStep]       = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');
  const [success, setSuccess] = useState('');
  const [reqDefs, setReqDefs] = useState([]);

  // Step 1 form
  const [basic, setBasic] = useState({
    full_name: '', phone: '', national_id: '',
    password: '', confirm_password: '', preferred_language: 'ar',
  });

  // Step 2 form — dynamic fields from requirement_definitions
  const [reqData, setReqData] = useState({});

  useEffect(() => {
    fetch(`${API}/auth/register/requirements`)
      .then(r => r.json())
      .then(d => setReqDefs(Array.isArray(d) ? d.filter(r => GROUPS_SHOW.includes(r.group_key)) : []))
      .catch(() => {});
  }, []);

  const setB = (k, v) => setBasic(p => ({ ...p, [k]: v }));
  const setR = (k, v) => setReqData(p => ({ ...p, [k]: v }));

  /* ── Step 1 validation ── */
  const validateStep1 = () => {
    if (!basic.full_name.trim()) return 'الاسم الكامل مطلوب';
    if (!/^(05|5)\d{8}$/.test(basic.phone.trim())) return 'رقم الجوال غير صحيح (يبدأ بـ 05)';
    if (basic.national_id && !/^[12]\d{9}$/.test(basic.national_id.trim())) return 'رقم الهوية يجب أن يكون 10 أرقام ويبدأ بـ 1 أو 2';
    if (basic.password.length < 6) return 'كلمة المرور يجب أن تكون 6 أحرف على الأقل';
    if (basic.password !== basic.confirm_password) return 'كلمة المرور غير متطابقة';
    return null;
  };

  /* ── Step 2 validation (required fields only) ── */
  const validateStep2 = () => {
    for (const req of reqDefs.filter(r => r.is_required)) {
      if (!reqData[req.field_key]) return `حقل "${req.field_name_ar}" مطلوب`;
    }
    return null;
  };

  const nextStep = () => {
    setError('');
    if (step === 1) {
      const err = validateStep1();
      if (err) { setError(err); return; }
    }
    if (step === 2) {
      const err = validateStep2();
      if (err) { setError(err); return; }
    }
    setStep(s => s + 1);
  };

  const submit = async () => {
    setError(''); setLoading(true);
    try {
      const res = await fetch(`${API}/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          full_name:          basic.full_name.trim(),
          phone:              basic.phone.trim(),
          national_id:        basic.national_id.trim() || null,
          password:           basic.password,
          preferred_language: basic.preferred_language,
          requirements_data:  reqData,
        }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.detail || 'خطأ'); return; }
      setSuccess(data.message || 'تم استلام طلبك بنجاح');
    } catch { setError('خطأ في الاتصال'); }
    finally   { setLoading(false); }
  };

  /* ── Success Screen ── */
  if (success) return (
    <div className="drv-auth">
      <div className="drv-auth__card" style={{textAlign:'center',padding:'40px 24px'}}>
        <div style={{fontSize:64,marginBottom:16}}>🎉</div>
        <h2 style={{marginBottom:8,color:'#1e293b'}}>تم استلام طلبك!</h2>
        <p style={{color:'#64748b',marginBottom:8,lineHeight:1.6}}>{success}</p>
        <p style={{color:'#94a3b8',fontSize:13,marginBottom:28}}>
          سيتم التواصل معك عند الموافقة على طلبك
        </p>
        <Link to="/driver/login" className="drv-btn drv-btn--primary" style={{display:'flex'}}>
          العودة لتسجيل الدخول
        </Link>
      </div>
    </div>
  );

  /* ── Steps Progress ── */
  const StepBar = () => (
    <div style={{display:'flex',alignItems:'center',justifyContent:'center',gap:8,marginBottom:24}}>
      {STEPS.map((s, i) => (
        <React.Fragment key={s.id}>
          <div style={{
            display:'flex',alignItems:'center',gap:6,
            opacity: step >= s.id ? 1 : 0.4,
            transition:'opacity 0.2s',
          }}>
            <div style={{
              width:28,height:28,borderRadius:'50%',display:'flex',
              alignItems:'center',justifyContent:'center',fontSize:12,
              fontWeight:'bold',
              background: step > s.id ? '#22c55e' : step === s.id ? '#3b82f6' : '#e2e8f0',
              color: step >= s.id ? 'white' : '#94a3b8',
            }}>
              {step > s.id ? '✓' : s.id}
            </div>
            <span style={{fontSize:12,color: step === s.id ? '#3b82f6' : '#94a3b8',fontWeight: step===s.id?'bold':'normal'}}>
              {s.title}
            </span>
          </div>
          {i < STEPS.length - 1 && (
            <div style={{flex:1,maxWidth:24,height:2,background: step > s.id ? '#22c55e' : '#e2e8f0',borderRadius:2}}/>
          )}
        </React.Fragment>
      ))}
    </div>
  );

  /* ── Render Step Fields ── */
  const renderRequirementField = (req) => {
    const val = reqData[req.field_key] || '';
    const common = {
      style:{width:'100%',padding:'10px 12px',border:'1px solid #e2e8f0',borderRadius:10,
             fontSize:14,outline:'none',background:'white',boxSizing:'border-box'},
      placeholder: req.placeholder_ar || '',
    };
    if (req.field_type === 'select' && req.options) {
      const opts = typeof req.options === 'string' ? JSON.parse(req.options) : req.options;
      return (
        <select {...common} value={val} onChange={e => setR(req.field_key, e.target.value)}>
          <option value="">— اختر —</option>
          {opts.map(o => (
            <option key={o.value} value={o.value}>{o.label_ar || o.label_en}</option>
          ))}
        </select>
      );
    }
    if (req.field_type === 'date') {
      return <input type="date" {...common} value={val} onChange={e => setR(req.field_key, e.target.value)}/>;
    }
    if (req.field_type === 'file') {
      return (
        <div>
          <input type="file"
            accept={(req.file_types || 'image/*,application/pdf').split(',').map(t => `.${t.trim()}`).join(',')}
            onChange={e => setR(req.field_key, e.target.files[0]?.name || '')}
            style={{fontSize:13,padding:'8px 0'}}/>
          <small style={{fontSize:11,color:'#94a3b8',display:'block',marginTop:2}}>
            {req.file_types ? `الصيغ المسموحة: ${req.file_types}` : 'JPG، PNG، PDF'}
            {req.max_file_size ? ` | الحجم الأقصى: ${(req.max_file_size/1024).toFixed(0)} MB` : ''}
          </small>
        </div>
      );
    }
    return (
      <input type={req.field_type === 'phone' ? 'tel' : 'text'} {...common}
        dir={req.field_type === 'phone' ? 'ltr' : undefined}
        value={val} onChange={e => setR(req.field_key, e.target.value)}/>
    );
  };

  // Group requirements by group
  const groupedReqs = reqDefs.reduce((acc, r) => {
    const g = r.group_key || 'other';
    if (!acc[g]) acc[g] = { name: r.group_name_ar, items: [] };
    acc[g].items.push(r);
    return acc;
  }, {});

  return (
    <div className="drv-auth" dir="rtl" style={{justifyContent:'flex-start',paddingTop:24,paddingBottom:40}}>
      {/* Logo */}
      <div className="drv-auth__logo">
        <svg viewBox="0 0 40 40" fill="none">
          <circle cx="20" cy="20" r="20" fill="#3b82f6"/>
          <path d="M20 8 C14 14 10 18 10 23 a10 10 0 0 0 20 0 C30 18 26 14 20 8Z" fill="white"/>
        </svg>
        <div>
          <span>Watrly</span>
          <small style={{display:'block',color:'#64748b'}}>تسجيل سائق جديد</small>
        </div>
      </div>

      <div className="drv-auth__card" style={{padding:'24px 20px'}}>
        <StepBar/>

        {/* ── Step 1: Basic Info ── */}
        {step === 1 && (
          <div>
            <h3 style={{margin:'0 0 4px',fontSize:18,color:'#1e293b'}}>👤 المعلومات الأساسية</h3>
            <p style={{color:'#64748b',fontSize:13,marginBottom:20}}>أدخل بياناتك الشخصية للتسجيل</p>

            <div className="drv-field">
              <label>الاسم الكامل *</label>
              <input value={basic.full_name} onChange={e=>setB('full_name',e.target.value)} placeholder="محمد أحمد العلي" required/>
            </div>
            <div className="drv-field">
              <label>رقم الجوال *</label>
              <input dir="ltr" value={basic.phone} onChange={e=>setB('phone',e.target.value)} placeholder="05XXXXXXXX" required/>
            </div>
            <div className="drv-field">
              <label>رقم الهوية الوطنية / الإقامة</label>
              <input dir="ltr" value={basic.national_id} onChange={e=>setB('national_id',e.target.value)} placeholder="1XXXXXXXXX"/>
            </div>
            <div className="drv-field">
              <label>كلمة المرور *</label>
              <input type="password" value={basic.password} onChange={e=>setB('password',e.target.value)} placeholder="6 أحرف على الأقل" minLength={6}/>
            </div>
            <div className="drv-field">
              <label>تأكيد كلمة المرور *</label>
              <input type="password" value={basic.confirm_password} onChange={e=>setB('confirm_password',e.target.value)} placeholder="أعد كتابة كلمة المرور"/>
            </div>
            <div className="drv-field">
              <label>اللغة المفضلة</label>
              <select value={basic.preferred_language} onChange={e=>setB('preferred_language',e.target.value)}>
                <option value="ar">العربية</option>
                <option value="en">English</option>
              </select>
            </div>
          </div>
        )}

        {/* ── Step 2: Requirements ── */}
        {step === 2 && (
          <div>
            <h3 style={{margin:'0 0 4px',fontSize:18,color:'#1e293b'}}>📄 الوثائق المطلوبة</h3>
            <p style={{color:'#64748b',fontSize:13,marginBottom:20}}>
              الحقول المميزة بـ * إلزامية. يمكنك رفع الوثائق لاحقاً من ملفك الشخصي.
            </p>
            {Object.keys(groupedReqs).length === 0 ? (
              <div style={{textAlign:'center',padding:'20px',color:'#94a3b8'}}>
                <p>لا توجد وثائق مطلوبة في هذه المرحلة</p>
              </div>
            ) : Object.entries(groupedReqs).map(([gk, g]) => (
              <div key={gk} style={{marginBottom:20}}>
                <div style={{
                  fontSize:12,fontWeight:'bold',color:'#64748b',textTransform:'uppercase',
                  letterSpacing:'0.05em',marginBottom:12,paddingBottom:6,
                  borderBottom:'1px solid #f1f5f9',
                }}>
                  {g.name}
                </div>
                {g.items.map(req => (
                  <div key={req.field_key} className="drv-field">
                    <label>
                      {req.field_name_ar}
                      {req.is_required && <span style={{color:'#ef4444',marginRight:4}}>*</span>}
                    </label>
                    {renderRequirementField(req)}
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}

        {/* ── Step 3: Confirm ── */}
        {step === 3 && (
          <div>
            <h3 style={{margin:'0 0 4px',fontSize:18,color:'#1e293b'}}>✅ مراجعة وتأكيد</h3>
            <p style={{color:'#64748b',fontSize:13,marginBottom:20}}>راجع بياناتك قبل الإرسال</p>

            <div style={{background:'#f8fafc',borderRadius:12,padding:16,marginBottom:16}}>
              <p style={{fontSize:12,fontWeight:'bold',color:'#64748b',marginBottom:10}}>المعلومات الأساسية</p>
              {[
                ['الاسم', basic.full_name],
                ['الجوال', basic.phone],
                ['رقم الهوية', basic.national_id || '—'],
              ].map(([k, v]) => (
                <div key={k} style={{display:'flex',justifyContent:'space-between',marginBottom:6,fontSize:14}}>
                  <span style={{color:'#94a3b8'}}>{k}</span>
                  <span style={{fontWeight:'bold',color:'#1e293b'}} dir="ltr">{v}</span>
                </div>
              ))}
            </div>

            <div style={{
              background:'#eff6ff',borderRadius:12,padding:14,
              border:'1px solid #bfdbfe',marginBottom:20,
            }}>
              <p style={{fontSize:13,color:'#1e40af',margin:0,lineHeight:1.6}}>
                📋 بعد التسجيل سيتم مراجعة طلبك من قِبَل شركة المياه أو مشرفي المنصة.
                سيتم إشعارك عند الموافقة.
              </p>
            </div>
          </div>
        )}

        {/* Error */}
        {error && (
          <div style={{
            background:'#fef2f2',border:'1px solid #fecaca',borderRadius:10,
            padding:'10px 14px',marginBottom:14,color:'#dc2626',fontSize:13,
          }}>
            {error}
          </div>
        )}

        {/* Navigation Buttons */}
        <div style={{display:'flex',gap:10,marginTop:8}}>
          {step > 1 && (
            <button className="drv-btn" onClick={() => setStep(s => s - 1)}
              style={{flex:1,background:'#f1f5f9',color:'#475569',border:'none'}}>
              ← السابق
            </button>
          )}
          {step < 3 ? (
            <button className="drv-btn drv-btn--primary" onClick={nextStep} style={{flex:2}}>
              التالي →
            </button>
          ) : (
            <button className="drv-btn drv-btn--primary" onClick={submit} disabled={loading} style={{flex:2}}>
              {loading ? <span className="drv-spinner drv-spinner--sm"/> : 'إرسال الطلب 🚀'}
            </button>
          )}
        </div>

        <div style={{marginTop:16, textAlign:'center'}}>
          <Link to="/driver/login" className="drv-link">لديك حساب؟ سجّل دخول</Link>
        </div>
      </div>
    </div>
  );
}
